// „Zähler“ in der Hausmeister-App (#616): Liste aller Zähler mit letztem Stand, Stand erfassen (mit Foto)
// und neuen Zähler anlegen. Nur sichtbar mit dem Modul „Zähler“ (Team → Berechtigungen);
// bearbeiten und löschen bleibt im Büro.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Camera, CaretLeft, Check, CircleNotch, Gauge, MagnifyingGlass, MapPin, Plus, X } from '@phosphor-icons/react';
import {
  erfasseStand, ladeFotoHoch, ladeObjekte, ladeZaehler, legeZaehlerAn, objektText, pruefeStand, sucheZaehler,
  type HmObjekt, type HmZaehler,
} from '../../lib/hausmeister/zaehler';
import { typInfo, ZAEHLER_TYPEN } from '../../lib/zaehler/typen';
import { kurzDatum, standText } from '../../lib/zaehler/liste';
import { EIGENER_TYP, nummerMitPraefix } from '../../lib/zaehler/formular';
import { leseZahl } from '../../lib/immobilien/mietverhaeltnisFormular';
import { heuteIso } from '../../lib/immobilien/format';
import { GlasWidget, IconChip, LeerZustand, SeitenTitel, StatusChip, type PortalFarbe } from '../../components/portal/bausteine';

const TYP_FARBE: Record<string, PortalFarbe> = {
  strom: 'strom', wasser: 'wasser', heizung: 'heizung', fernwaerme: 'heizung', gas: 'orange', oel: 'braun', allgemein: 'grau',
};
const farbeFuer = (typ: string): PortalFarbe => TYP_FARBE[typ] ?? 'lila';

const feldStil: React.CSSProperties = { width: '100%', colorScheme: 'dark' };
const label: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12.5, fontWeight: 700, color: 'var(--p-w60)', minWidth: 0 };

type Ansicht = { art: 'liste' } | { art: 'neu' } | { art: 'stand'; zaehler: HmZaehler };

export default function ZaehlerBereich({ onFehler, onMeldung }: { onFehler: (t: string | null) => void; onMeldung: (t: string) => void }) {
  const [zaehler, setZaehler] = useState<HmZaehler[]>([]);
  const [objekte, setObjekte] = useState<HmObjekt[]>([]);
  const [laedt, setLaedt] = useState(true);
  const [suche, setSuche] = useState('');
  const [objektFilter, setObjektFilter] = useState('');
  const [ansicht, setAnsicht] = useState<Ansicht>({ art: 'liste' });

  const laden = useCallback(async () => {
    setLaedt(true);
    try {
      const [z, o] = await Promise.all([ladeZaehler(), ladeObjekte()]);
      setZaehler(z);
      setObjekte(o);
    } catch (e) {
      onFehler(`Zähler konnten nicht geladen werden: ${(e as Error).message}`);
    } finally {
      setLaedt(false);
    }
  }, [onFehler]);
  useEffect(() => { void laden(); }, [laden]);

  const objektMap = useMemo(() => new Map(objekte.map(o => [o.id, o])), [objekte]);
  const sichtbar = useMemo(() => sucheZaehler(zaehler, objektMap, suche, objektFilter), [zaehler, objektMap, suche, objektFilter]);

  const fertig = (text: string) => { setAnsicht({ art: 'liste' }); onMeldung(text); void laden(); window.scrollTo(0, 0); };
  const zurueck = () => { setAnsicht({ art: 'liste' }); window.scrollTo(0, 0); };

  if (ansicht.art === 'neu') {
    return <NeuerZaehlerFormular objekte={objekte} vorwahl={objektFilter} onZurueck={zurueck} onFehler={onFehler} onFertig={fertig} />;
  }
  if (ansicht.art === 'stand') {
    return <StandFormular zaehler={ansicht.zaehler} objekt={objektMap.get(ansicht.zaehler.objekt_id)} onZurueck={zurueck} onFehler={onFehler} onFertig={fertig} />;
  }

  return (
    <div className="p-rein" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <SeitenTitel oben={laedt ? 'Wird geladen …' : `${sichtbar.length} von ${zaehler.length}`} titel="Zähler"
        rechts={
          <button type="button" className="p-knopf p-knopf-gold" onClick={() => { onFehler(null); setAnsicht({ art: 'neu' }); }}
            data-tip="Neuen Zähler anlegen" style={{ height: 42 }}>
            <Plus size={16} weight="bold" />Neuer Zähler
          </button>
        } />

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <label style={{ position: 'relative', flex: '2 1 220px', minWidth: 0 }}>
          <MagnifyingGlass size={16} style={{ position: 'absolute', left: 14, top: 16, color: 'var(--p-w45)' }} aria-hidden />
          <input className="p-feld" value={suche} onChange={e => setSuche(e.target.value)} placeholder="Nummer, Standort, Wohnung …"
            aria-label="Zähler suchen" style={{ ...feldStil, paddingLeft: 38 }} />
        </label>
        <select className="p-feld" value={objektFilter} onChange={e => setObjektFilter(e.target.value)} aria-label="Nach Objekt filtern"
          style={{ ...feldStil, flex: '1 1 200px', width: 'auto' }}>
          <option value="">Alle Objekte</option>
          {objekte.map(o => <option key={o.id} value={o.id}>{objektText(o)}</option>)}
        </select>
      </div>

      {laedt && zaehler.length === 0 ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0', color: 'var(--p-w40)' }} aria-busy="true" aria-label="Lädt">
          <CircleNotch size={24} weight="bold" className="animate-spin" />
        </div>
      ) : sichtbar.length === 0 ? (
        <LeerZustand icon={Gauge} text={zaehler.length ? 'Kein Zähler passt zur Suche.' : 'Noch keine Zähler.'} />
      ) : (
        <div className="hm-karten">
          {sichtbar.map(z => {
            const info = typInfo(z.typ);
            const o = objektMap.get(z.objekt_id);
            return (
              <button key={z.id} type="button" className="p-glas p-hebt" data-tip="Zählerstand erfassen"
                onClick={() => { onFehler(null); setAnsicht({ art: 'stand', zaehler: z }); window.scrollTo(0, 0); }}
                style={{ width: '100%', textAlign: 'left', color: 'var(--p-w)', cursor: 'pointer', border: 'none', padding: 16, display: 'flex', flexDirection: 'column', gap: 10, opacity: z.aktiv ? 1 : 0.6 }}>
                <div className="p-glas-rand" />
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                  <IconChip farbe={farbeFuer(z.typ)} icon={info.icon} groesse={44} />
                  <span style={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <span style={{ fontSize: 15, fontWeight: 800, overflowWrap: 'anywhere' }}>{z.zaehler_nummer}</span>
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--p-w55)', display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
                      <MapPin size={12} weight="fill" style={{ flexShrink: 0 }} />
                      <span className="p-kuerzen">{[objektText(o), z.haus, z.einheit_bezeichnung, z.standort].filter(Boolean).join(' · ')}</span>
                    </span>
                  </span>
                  <StatusChip ton={z.aktiv ? 'neutral' : 'warn'}>{z.aktiv ? info.label : 'inaktiv'}</StatusChip>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, fontSize: 12.5, fontWeight: 700, color: 'var(--p-w60)' }}>
                  <span>{z.letzte_datum ? `Letzter Stand ${kurzDatum(z.letzte_datum)}` : 'Noch kein Stand erfasst'}</span>
                  {z.letzter_wert !== null && <b className="p-zahl" style={{ fontSize: 15, color: 'var(--p-w)' }}>{standText(z.letzter_wert, z.einheit)}</b>}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Kopf({ titel, oben, onZurueck }: { titel: string; oben: string; onZurueck: () => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <button type="button" className="p-rund" onClick={onZurueck} data-tip="Zurück zur Zählerliste" aria-label="Zurück"><CaretLeft size={18} weight="bold" /></button>
      <div style={{ flex: 1, minWidth: 0 }}><SeitenTitel oben={oben} titel={titel} /></div>
    </div>
  );
}

/** Foto auswählen (Kamera auf dem Telefon) mit Vorschau. */
function FotoWahl({ datei, onDatei }: { datei: File | null; onDatei: (f: File | null) => void }) {
  const ref = useRef<HTMLInputElement>(null);
  const vorschau = useMemo(() => (datei ? URL.createObjectURL(datei) : null), [datei]);
  useEffect(() => () => { if (vorschau) URL.revokeObjectURL(vorschau); }, [vorschau]);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <input ref={ref} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} aria-label="Foto auswählen"
        onChange={e => { onDatei(e.target.files?.[0] ?? null); e.target.value = ''; }} />
      {vorschau ? (
        <>
          <img src={vorschau} alt="Foto vom Zähler" style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 14 }} />
          <button type="button" className="p-knopf" onClick={() => onDatei(null)} data-tip="Foto entfernen"><X size={15} weight="bold" />Entfernen</button>
        </>
      ) : (
        <button type="button" className="p-knopf" onClick={() => ref.current?.click()} data-tip="Foto vom Zähler aufnehmen oder auswählen">
          <Camera size={16} weight="bold" />Foto (optional)
        </button>
      )}
    </div>
  );
}

function StandFormular({ zaehler: z, objekt, onZurueck, onFehler, onFertig }: {
  zaehler: HmZaehler; objekt: HmObjekt | undefined; onZurueck: () => void; onFehler: (t: string | null) => void; onFertig: (t: string) => void;
}) {
  const heute = heuteIso();
  const [datum, setDatum] = useState(heute);
  const [wert, setWert] = useState('');
  const [notiz, setNotiz] = useState('');
  const [foto, setFoto] = useState<File | null>(null);
  const [speichert, setSpeichert] = useState(false);
  const p = pruefeStand(wert, datum, heute, z);
  const info = typInfo(z.typ);

  const speichern = async () => {
    if (p.wert === null || speichert) return;
    setSpeichert(true);
    onFehler(null);
    try {
      const url = foto ? await ladeFotoHoch(z.id, foto) : null;
      await erfasseStand(z.id, datum, p.wert, url, notiz.trim());
      onFertig(`Zählerstand ${standText(p.wert, z.einheit)} für ${z.zaehler_nummer} gespeichert.`);
    } catch (e) {
      onFehler(`Zählerstand konnte nicht gespeichert werden: ${(e as Error).message}`);
    } finally {
      setSpeichert(false);
    }
  };

  return (
    <div className="p-rein" style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 640 }}>
      <Kopf oben={[objektText(objekt), z.haus, z.einheit_bezeichnung].filter(Boolean).join(' · ')} titel={z.zaehler_nummer} onZurueck={onZurueck} />
      <GlasWidget farbe={farbeFuer(z.typ)} icon={info.icon} titel={`${info.label}${z.standort ? ` · ${z.standort}` : ''}`} hebt={false}>
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--p-w60)' }}>
          {z.letzter_wert !== null && z.letzte_datum ? `Letzter Stand: ${standText(z.letzter_wert, z.einheit)} am ${kurzDatum(z.letzte_datum)}` : 'Noch kein Stand erfasst.'}
        </span>
        <form onSubmit={e => { e.preventDefault(); void speichern(); }} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <label style={{ ...label, flex: '1 1 150px' }}>Datum
              <input className="p-feld" type="date" value={datum} max={heute} onChange={e => setDatum(e.target.value)} style={feldStil} />
            </label>
            <label style={{ ...label, flex: '1 1 150px' }}>Zählerstand{z.einheit ? ` (${z.einheit})` : ''}
              <input className="p-feld p-zahl" value={wert} onChange={e => setWert(e.target.value)} inputMode="decimal" placeholder="0" autoFocus style={feldStil} />
            </label>
          </div>
          {wert && p.fehler && <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--p-fehler-ink)' }}>{p.fehler}</span>}
          {p.warnung && <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--p-ton-warn-fg)' }}>{p.warnung}</span>}
          <label style={label}>Notiz (optional)
            <input className="p-feld" value={notiz} onChange={e => setNotiz(e.target.value)} placeholder="z. B. Zähler schwer zugänglich" style={feldStil} />
          </label>
          <FotoWahl datei={foto} onDatei={setFoto} />
          <button type="submit" className="p-knopf p-knopf-gold" disabled={p.wert === null || speichert} data-tip="Zählerstand speichern">
            {speichert ? <CircleNotch size={16} weight="bold" className="animate-spin" /> : <Check size={16} weight="bold" />}Stand speichern
          </button>
        </form>
      </GlasWidget>
    </div>
  );
}

function NeuerZaehlerFormular({ objekte, vorwahl, onZurueck, onFehler, onFertig }: {
  objekte: HmObjekt[]; vorwahl: string; onZurueck: () => void; onFehler: (t: string | null) => void; onFertig: (t: string) => void;
}) {
  const heute = heuteIso();
  const [objektId, setObjektId] = useState(vorwahl);
  const [typ, setTyp] = useState('strom');
  const [eigenerTyp, setEigenerTyp] = useState('');
  const [einheit, setEinheit] = useState('kWh');
  const [nummer, setNummer] = useState('');
  const [vorschlag, setVorschlag] = useState('');
  const [hausId, setHausId] = useState('');
  const [wohnung, setWohnung] = useState('');
  const [standort, setStandort] = useState('');
  const [notiz, setNotiz] = useState('');
  const [stand, setStand] = useState('');
  const [foto, setFoto] = useState<File | null>(null);
  const [speichert, setSpeichert] = useState(false);
  const objekt = objekte.find(o => o.id === objektId);

  // Objektnummer als Präfix vorschlagen (wie im Büro), echte Eingaben bleiben stehen
  useEffect(() => {
    setNummer(alt => { const r = nummerMitPraefix(alt, vorschlag, objekt?.objektnummer); setVorschlag(r.vorschlag); return r.nummer; });
    setHausId('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objektId]);

  const typText = typ === EIGENER_TYP ? eigenerTyp.trim() : typ;
  const anfang = stand.trim() ? leseZahl(stand) : null;
  const fehler = !objektId ? 'Bitte ein Objekt wählen.'
    : !nummer.trim() || nummer === vorschlag ? 'Bitte die Zählernummer eingeben.'
      : !typText ? 'Bitte den Zählertyp angeben.'
        : stand.trim() && anfang === null ? 'Der Anfangsstand ist keine Zahl.' : null;

  const speichern = async () => {
    if (fehler || speichert) return;
    setSpeichert(true);
    onFehler(null);
    try {
      const id = await legeZaehlerAn({ objektId, nummer: nummer.trim(), typ: typText, einheit: einheit.trim(), standort: standort.trim(), hausId, wohnung: wohnung.trim(), notizen: notiz.trim() });
      let zusatz = '';
      if (anfang !== null) {
        try {
          const url = foto ? await ladeFotoHoch(id, foto) : null;
          await erfasseStand(id, heute, anfang, url, '');
        } catch (e) {
          zusatz = ` Anfangsstand/Foto konnte nicht gespeichert werden: ${(e as Error).message}`;
        }
      }
      onFertig(`Zähler ${nummer.trim()} angelegt.${zusatz}`);
    } catch (e) {
      onFehler(`Zähler konnte nicht angelegt werden: ${(e as Error).message}`);
    } finally {
      setSpeichert(false);
    }
  };

  return (
    <div className="p-rein" style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 640 }}>
      <Kopf oben="Zähler" titel="Neuer Zähler" onZurueck={onZurueck} />
      <GlasWidget farbe={farbeFuer(typ)} icon={typInfo(typ === EIGENER_TYP ? '' : typ).icon} titel="Angaben" hebt={false}>
        <form onSubmit={e => { e.preventDefault(); void speichern(); }} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <label style={label}>Objekt
            <select className="p-feld" value={objektId} onChange={e => setObjektId(e.target.value)} style={feldStil}>
              <option value="">Bitte wählen …</option>
              {objekte.map(o => <option key={o.id} value={o.id}>{objektText(o)}</option>)}
            </select>
          </label>
          {objekt && objekt.haeuser.length > 0 && (
            <label style={label}>Haus (optional)
              <select className="p-feld" value={hausId} onChange={e => setHausId(e.target.value)} style={feldStil}>
                <option value="">—</option>
                {objekt.haeuser.map(h => <option key={h.id} value={h.id}>{h.bezeichnung || [h.strasse, h.hausnummer].filter(Boolean).join(' ') || 'Haus'}</option>)}
              </select>
            </label>
          )}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <label style={{ ...label, flex: '1 1 160px' }}>Typ
              <select className="p-feld" value={typ} style={feldStil}
                onChange={e => { const t = e.target.value; setTyp(t); const std = ZAEHLER_TYPEN.find(x => x.key === t); if (std) setEinheit(std.einheit); }}>
                {ZAEHLER_TYPEN.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                <option value={EIGENER_TYP}>Sonstig …</option>
              </select>
            </label>
            <label style={{ ...label, flex: '1 1 110px' }}>Messeinheit
              <input className="p-feld" value={einheit} onChange={e => setEinheit(e.target.value)} placeholder="kWh" style={feldStil} />
            </label>
          </div>
          {typ === EIGENER_TYP && (
            <label style={label}>Eigener Typ
              <input className="p-feld" value={eigenerTyp} onChange={e => setEigenerTyp(e.target.value)} placeholder="z. B. Wärmemengenzähler" style={feldStil} />
            </label>
          )}
          <label style={label}>Zählernummer
            <input className="p-feld" value={nummer} onChange={e => setNummer(e.target.value)} placeholder="Nummer vom Typenschild" style={feldStil} />
          </label>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <label style={{ ...label, flex: '1 1 160px' }}>Wohnung / Einheit (optional)
              <input className="p-feld" value={wohnung} onChange={e => setWohnung(e.target.value)} placeholder="z. B. Whg 3 OG links" style={feldStil} />
            </label>
            <label style={{ ...label, flex: '1 1 160px' }}>Standort (optional)
              <input className="p-feld" value={standort} onChange={e => setStandort(e.target.value)} placeholder="z. B. Keller, Hausanschlussraum" style={feldStil} />
            </label>
          </div>
          <label style={label}>Anfangsstand heute (optional){einheit ? ` (${einheit})` : ''}
            <input className="p-feld p-zahl" value={stand} onChange={e => setStand(e.target.value)} inputMode="decimal" placeholder="0" style={feldStil} />
          </label>
          <label style={label}>Notiz (optional)
            <input className="p-feld" value={notiz} onChange={e => setNotiz(e.target.value)} style={feldStil} />
          </label>
          {/* Das Foto gehört zum Anfangsstand */}
          {anfang !== null && <FotoWahl datei={foto} onDatei={setFoto} />}
          {fehler && (nummer || objektId) && <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--p-w55)' }}>{fehler}</span>}
          <button type="submit" className="p-knopf p-knopf-gold" disabled={!!fehler || speichert} data-tip="Zähler anlegen">
            {speichert ? <CircleNotch size={16} weight="bold" className="animate-spin" /> : <Check size={16} weight="bold" />}Zähler anlegen
          </button>
        </form>
      </GlasWidget>
    </div>
  );
}
